import cn from 'classnames';
import { forwardRef } from 'react';

import { useFieldId } from '../../../utils';
import { BaseField, BaseInput, FieldLabel } from '../../internal';

import type { ForwardedRef } from 'react';

import type { BaseInputProps } from '../../internal';
import type { InputFieldProps } from './input-field.types';

/**
 * The input field includes a label, a helper text and an input field where users can type one line of text.
 * The input field component typically appears in forms and dialogs.
 *
 * ## Usage
 *
 * ### Basic
 * ```jsx
 * import { InputField } from '@bloomreach/react-banana-ui';
 *
 * export default function MyComponent() {
 *   return <InputField placeholder="Placeholder..." />;
 * }
 * ```
 *
 * ### With label and helper text
 * ```jsx
 * import { InputField } from '@bloomreach/react-banana-ui';
 *
 * export default function MyComponent() {
 *   return (
 *     <InputField
 *       label="Field with a label"
 *       helperText="This is helper text"
 *     />
 *   );
 * }
 * ```
 */
const InputField = forwardRef((props: InputFieldProps, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    className,
    clearable,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    inputProps,
    label,
    leftElement,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    required,
    rightElement,
    tooltip,
    value,
    ...rest
  } = props;

  const controlId = useFieldId(id ?? inputProps?.id, 'rbui-input-field');

  const baseInputProps: BaseInputProps = {
    clearable,
    defaultValue,
    disabled,
    error,
    id: controlId,
    inputProps,
    leftElement,
    name,
    onBlur,
    onChange,
    onFocus,
    placeholder,
    readOnly,
    required,
    rightElement,
    value,
  };

  const fieldLabel = label && (
    <FieldLabel htmlFor={controlId} required={required} tooltip={tooltip}>
      {label === ' ' ? <span>&nbsp;</span> : label}
    </FieldLabel>
  );

  return (
    <BaseField
      {...rest}
      className={cn('rbui-input-field', className)}
      control={<BaseInput {...baseInputProps} inputRef={ref} />}
      error={error}
      helperText={helperText}
      label={fieldLabel}
    />
  );
});

InputField.displayName = 'InputField';

export default InputField;
